import { Box, Typography } from '@mui/material';
import { getTimeLabel, getTimelineHourMarks } from '../../../helpers/time';
import { LABEL_WIDTH, MIN_HOUR_ROW_HEIGHT } from '../layout';

const GRID_SX = {
  position: 'relative',
  zIndex: 0,
  display: 'grid',
  height: '100%',
  minHeight: '100%',
} as const;

const HOUR_ROW_SX = {
  position: 'relative',
  minHeight: MIN_HOUR_ROW_HEIGHT,
} as const;

const HOUR_MARK_SX = {
  pointerEvents: 'none',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 0,
} as const;

const END_HOUR_MARK_SX = {
  ...HOUR_MARK_SX,
  top: '100%',
} as const;

const TIME_LABEL_SX = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: LABEL_WIDTH,
  pr: 2,
  textAlign: 'right',
  lineHeight: 1,
  transform: 'translateY(-50%)',
} as const;

const HOUR_LINE_SX = {
  position: 'absolute',
  top: 0,
  left: LABEL_WIDTH,
  right: 0,
  height: '1px',
  backgroundColor: 'var(--mui-palette-border-default)',
  boxShadow: '0px 1.5px 2px 0px var(--mui-palette-border-default)',
  transform: 'translateY(-50%)',
} as const;

type HourMarkProps = Readonly<{
  hour: number;
  isEnd?: boolean;
}>;

const HourMark = ({ hour, isEnd = false }: HourMarkProps) => (
  <Box sx={isEnd ? END_HOUR_MARK_SX : HOUR_MARK_SX}>
    <Typography variant="body2" color="textPrimary" sx={TIME_LABEL_SX}>
      {getTimeLabel(hour * 60)}
    </Typography>
    <Box sx={HOUR_LINE_SX} />
  </Box>
);

const TimelineGrid = () => {
  const hourMarks = getTimelineHourMarks();
  const hourIntervals = hourMarks.slice(0, -1);
  const endHour = hourMarks[hourMarks.length - 1];

  return (
    <Box
      sx={{
        ...GRID_SX,
        gridTemplateRows: `repeat(${hourIntervals.length}, minmax(${MIN_HOUR_ROW_HEIGHT}px, 1fr))`,
      }}
    >
      {hourIntervals.map(hour => (
        <Box key={hour} data-focus-time-hour="true" sx={HOUR_ROW_SX}>
          <HourMark hour={hour} />
        </Box>
      ))}

      {endHour ? <HourMark hour={endHour} isEnd /> : null}
    </Box>
  );
};

export default TimelineGrid;
